import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { BrButton, BrCard } from '@govbr-ds/react-components'
import { useFeatureFlag, FEATURE_FLAGS } from '../lib/featureFlags'
import { LanguageToggle } from '../components/LanguageToggle'

export function HomePage() {
  const { t } = useTranslation()
  const { user, userRole, signInWithGoogle, loading } = useAuth()
  const [isSigningIn, setIsSigningIn] = useState(false)
  const navigate = useNavigate();

  // Feature flag for new admin card design
  const showNewAdminCard = useFeatureFlag(FEATURE_FLAGS.NEW_ADMIN_CARD);

  // Redirect based on role after login
  useEffect(() => {
    if (user && userRole && !loading) {
      console.log('HomePage: Redirecionando usuário', user.email, 'role:', userRole);
      switch (userRole) {
        case 'admin':
          navigate('/dashboard');
          break;
        case 'expert':
          navigate('/especialista');
          break;
        case 'participant':
          navigate('/participante');
          break;
      }
    }
  }, [user, userRole, loading, navigate]);

  const handleSignIn = async () => {
    if (isSigningIn) return;
    setIsSigningIn(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('HomePage: Erro no login:', error);
      setIsSigningIn(false);
    }
  }

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen d-flex align-items-center justify-content-center bg-gray-2">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-60">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen d-flex align-items-center justify-content-center bg-gray-2 py-4">
      <div className="container-lg">
        <div className="row justify-content-center">
          <div className="col-12 col-md-10 col-lg-8 col-xl-6">
            {/* Language Toggle */}
            <div className="d-flex justify-content-end mb-3">
              <LanguageToggle variant="primary" />
            </div>

            {/* Logo/Header */}
            <div className="text-center mb-8">
              <img
                src="/logo-lumen.svg"
                alt="Lumen Framework"
                className="mx-auto mb-3"
                style={{ width: '80px', height: '80px' }}
              />
              <h1 className="text-5xl font-bold text-primary-default mb-1">
                {t('home.title')}
              </h1>
              <span className="text-gray-60 small">
                {t('home.subtitle')}
              </span>
            </div>



            {/* About Card - Main */}
            <BrCard className="mb-4">
              <div className="card-content text-center">
                <h2 className="text-xl font-semibold text-gray-80 mb-2">
                  {t('home.accessSystem')}
                </h2>
                <p className="text-gray-60 text-sm mb-4">
                  {t('home.evaluationClosed')}
                </p>
                <p className="text-xs text-center text-gray-60 mt-4 mb-0">
                  {t('home.universityBrasilia')}
                </p>
              </div>
            </BrCard>

            {/* About Card */}
            <BrCard className="mb-4">
              <div className="card-content">
                <h3 className="text-lg font-semibold text-primary-default mb-2">
                  {t('home.title')}
                </h3>
                <p className="text-sm text-gray-60 mb-3">
                  {t('home.aboutShort')}
                </p>
                <div className="d-flex gap-2 flex-wrap">
                  <span
                    className="d-inline-flex align-items-center gap-2 px-2 py-1 rounded text-xs"
                    style={{ backgroundColor: 'rgba(19, 81, 180, 0.1)', color: '#1351b4' }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1351b4" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <circle cx="12" cy="12" r="6" />
                      <circle cx="12" cy="12" r="2" />
                    </svg>
                    {t('home.badgePersonalization')}
                  </span>
                  <span
                    className="d-inline-flex align-items-center gap-2 px-2 py-1 rounded text-xs"
                    style={{ backgroundColor: 'rgba(19, 81, 180, 0.1)', color: '#1351b4' }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1351b4" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="4" r="2" />
                      <path d="M12 6v6m0 0l-4 6m4-6l4 6M6 10h12" />
                    </svg>
                    {t('home.badgeAccessibility')}
                  </span>
                  <span
                    className="d-inline-flex align-items-center gap-2 px-2 py-1 rounded text-xs"
                    style={{ backgroundColor: 'rgba(19, 81, 180, 0.1)', color: '#1351b4' }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1351b4" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z" />
                    </svg>
                    {t('home.badgeDesignSystem')}
                  </span>
                </div>
              </div>
            </BrCard>

            {/* Admin Access - Feature Flag Controlled */}
            {showNewAdminCard ? (
              /* New Design - Redesigned Admin Card */
              <BrCard>
                <div className="card-content">
                  {/* Header with Icon */}
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span style={{ fontSize: '20px', color: '#1351B4' }}>🔐</span>
                    <h3 className="text-lg font-semibold text-gray-80 m-0">
                      {t('home.adminAccess')}
                    </h3>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-60 mb-4" style={{ lineHeight: '1.5' }}>
                    {t('home.adminDescription')}
                  </p>

                  {/* Google Sign-in Button */}
                  <button
                    onClick={handleSignIn}
                    disabled={isSigningIn}
                    className="w-full bg-pure-0 border border-gray-20 rounded-lg px-4 py-3
                             d-flex align-items-center justify-content-center gap-3
                             hover-bg-gray-2 transition-colors"
                    style={{ minHeight: '48px' }}
                  >
                    {isSigningIn ? (
                      <span className="spinner" />
                    ) : (
                      <>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ width: '20px', height: '20px', minWidth: '20px', flexShrink: 0 }}>
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        <span className="text-base font-medium text-gray-80">
                          {isSigningIn ? t('common.signingIn') : t('common.continueWithGoogle')}
                        </span>
                      </>
                    )}
                  </button>

                  {/* Info Badge */}
                  <div
                    className="d-flex align-items-center gap-2 mt-4 p-2 rounded"
                    style={{ backgroundColor: 'rgba(21, 91, 203, 0.1)' }}
                  >
                    <span style={{ fontSize: '14px', color: '#1351B4' }}>ⓘ</span>
                    <span className="text-xs text-gray-80">
                      {t('home.restrictedAccess')}
                    </span>
                  </div>
                </div>
              </BrCard>
            ) : (
              /* Fallback - Simple Admin Card (Old Design) */
              <BrCard>
                <div className="card-content text-center">
                  <h3 className="text-base font-semibold text-gray-80 mb-3">
                    {t('home.adminAccess')}
                  </h3>
                  <BrButton
                    primary
                    onClick={handleSignIn}
                    disabled={isSigningIn}
                    className="w-full"
                  >
                    {isSigningIn ? t('common.signingIn') : t('common.signIn')}
                  </BrButton>
                </div>
              </BrCard>
            )}
          </div>
        </div>
      </div >
    </div >
  )
}
